import React, { useEffect, useRef, useState } from 'react';
import { motion, useInView, animate } from 'framer-motion';
import { ImageOff } from 'lucide-react';

const easeLuxury = [0.22, 1, 0.36, 1];

const STATS = [
  { value: 25, suffix: '+', label: 'Yıl Deneyim' },
  { value: 1000, suffix: '+', label: 'Mutlu Müşteri' },
  { value: 5, suffix: '.0★', label: 'Google Puanı', decimals: 1 },
];

function CountUp({ value, suffix = '', decimals = 0 }) {
  const ref = useRef(null);
  const inView = useInView(ref, { once: true, margin: '-100px' });
  const [display, setDisplay] = useState('0');

  useEffect(() => {
    if (!inView) return;
    const controls = animate(0, value, {
      duration: 1.6,
      ease: easeLuxury,
      onUpdate: (latest) => setDisplay(latest.toFixed(decimals)),
    });
    return () => controls.stop();
  }, [inView, value, decimals]);

  return <span ref={ref}>{display}{suffix}</span>;
}

export default function About() {
  return (
    <section id="about" className="bg-bg-primary py-20 sm:py-28 lg:py-36">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-10">
        <div className="grid lg:grid-cols-2 gap-10 sm:gap-16 items-center">
          {/* Visual */}
          <motion.div
            initial={{ opacity: 0, x: -40 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true, margin: '-100px' }}
            transition={{ duration: 0.8, ease: easeLuxury }}
            className="relative aspect-[4/5] rounded-2xl overflow-hidden border border-border-soft bg-gradient-to-br from-bg-secondary via-[#1a1a1a] to-black flex flex-col items-center justify-center"
          >
            <ImageOff className="text-white/15 mb-4" size={56} strokeWidth={1} />
            <p className="text-text-muted text-sm tracking-widest uppercase">Görsel Eklenecek</p>
            <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent" />
          </motion.div>

          {/* Content */}
          <motion.div
            initial={{ opacity: 0, y: 40 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: '-100px' }}
            transition={{ duration: 0.8, ease: easeLuxury, delay: 0.1 }}
            className="space-y-8"
          >
            <div className="space-y-3 sm:space-y-4">
              <p className="text-accent-gold font-semibold tracking-[0.3em] uppercase text-sm">Hikayemiz</p>
              <h2 className="text-3xl sm:text-4xl md:text-6xl font-black text-white tracking-tightest leading-[1.05]">
                25 Yıllık<br />Ustalık
              </h2>
            </div>

            <p className="text-text-muted font-light leading-relaxed max-w-xl text-sm sm:text-base">
              <span className="text-white font-medium">Öncü Boya</span>, 2001 yılından beri Tekkeköy'de
              araç boyama ve boya ürünleri alanında güvenilir hizmet sunmaktadır. Fabrika çıkışı
              kalitesinde işçilik, dürüst fiyatlandırma ve müşteri memnuniyeti odaklı yaklaşımımızla
              25 yıldır bölgenin tercih edilen ismi olduk.
            </p>

            <div className="grid grid-cols-3 gap-3 sm:gap-6 pt-4 border-t border-border-soft">
              {STATS.map((stat) => (
                <div key={stat.label}>
                  <p className="text-2xl sm:text-4xl md:text-5xl font-black text-accent-gold tracking-tight">
                    <CountUp value={stat.value} suffix={stat.suffix} decimals={stat.decimals || 0} />
                  </p>
                  <p className="text-text-muted text-[11px] sm:text-xs md:text-sm font-light mt-2 tracking-wide">{stat.label}</p>
                </div>
              ))}
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );
}
